import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import {
  FormControl,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { Group } from "../../types/Group.ts";
import { timeZoneFullNames } from "../../utils/timeZones.ts";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import { TimePicker } from "../TimePicker/TimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

interface CYParametersStepProps {
  dateRangeDidChange: Dispatch<SetStateAction<{ start: string; end?: string } | undefined>>;
  initialDateRange?: { start: string; end?: string };
  group?: Group;
}

interface ConstructData {
  cyActivation: string;
  endTime: string | null;
  endDate: Date | null;
  laterStartDate: Date | null;
  laterEndDate: Date | null;
  laterStartTime: string | null;
  laterEndTime: string | null;
}

enum CYActivation {
  now = "now",
  later = "later",
}

const combineDateAndTime = (date: Date, time: string): string => {
  const [hoursStr, minutesStr] = time.split(":");
  const [minutes, ampm] = minutesStr.split(" ");
  let hours = parseInt(hoursStr, 10);
  if (ampm.toLowerCase() === "pm" && hours !== 12) hours += 12;
  if (ampm.toLowerCase() === "am" && hours === 12) hours = 0;
  const dateTime = new Date(date);
  dateTime.setHours(hours, parseInt(minutes, 10), 0, 0);
  return dayjs(dateTime).format();
};

const constructDateRange = ({
  cyActivation,
  endTime,
  endDate,
  laterEndDate,
  laterEndTime,
  laterStartDate,
  laterStartTime,
}: ConstructData) => {
  if (cyActivation === CYActivation.now.toString()) {
    const now = new Date();
    let end: string | undefined;
    if (endDate && endTime) {
      const [hoursStr, minutesStr] = endTime.split(":");
      const [minutes, ampm] = minutesStr.split(" ");
      let hours = parseInt(hoursStr, 10);
      if (ampm.toLowerCase() === "pm" && hours !== 12) hours += 12;
      if (ampm.toLowerCase() === "am" && hours === 12) hours = 0;
      const endDateTime = new Date(endDate);
      endDateTime.setHours(hours, parseInt(minutes, 10), 0, 0);
      end = dayjs(endDateTime).format();
    }
    return {
      start: dayjs(now).format(),
      end,
    };
  } else {
    if (!laterStartDate || !laterEndDate || !laterStartTime || !laterEndTime) {
      return undefined;
    }
    const start = combineDateAndTime(laterStartDate, laterStartTime);
    const end = combineDateAndTime(laterEndDate, laterEndTime);
    return { start, end };
  }
};

const filterEndTimes = (endDate: Date | null, time: string) => {
  if (!endDate) return true;
  const now = new Date();
  const [hours, minutes] = time.split(":");
  const [minutesPart, ampm] = minutes.split(" ");
  let hour = parseInt(hours, 10);
  if (ampm.toLowerCase() === "pm" && hour !== 12) hour += 12;
  if (ampm.toLowerCase() === "am" && hour === 12) hour = 0;
  const timeDate = new Date(endDate);
  timeDate.setHours(hour, parseInt(minutesPart, 10), 0, 0);
  return timeDate > now;
};

const CYParametersStep: React.FC<CYParametersStepProps> = ({
  dateRangeDidChange,
  initialDateRange,
  group,
}) => {
  const [cyActivation, setCyActivation] = useState<string>(
    initialDateRange?.start && initialDateRange?.end
      ? CYActivation.later.toString()
      : CYActivation.now.toString(),
  );

  // Active now states
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);

  // Active later states
  const [laterStartDate, setLaterStartDate] = useState<Date | null>(
    initialDateRange?.start ? new Date(initialDateRange.start) : null,
  );
  const [laterStartTime, setLaterStartTime] = useState<string | null>(
    initialDateRange?.start ? dayjs(initialDateRange.start).format("hh:mm A") : null,
  );
  const [laterEndDate, setLaterEndDate] = useState<Date | null>(
    initialDateRange?.end ? new Date(initialDateRange.end) : null,
  );
  const [laterEndTime, setLaterEndTime] = useState<string | null>(
    initialDateRange?.end ? dayjs(initialDateRange.end).format("hh:mm A") : null,
  );

  useEffect(() => {
    const constructedDateRange = constructDateRange({
      cyActivation,
      endDate,
      endTime,
      laterStartDate,
      laterStartTime,
      laterEndDate,
      laterEndTime,
    });
    dateRangeDidChange(constructedDateRange);
  }, [
    cyActivation,
    dateRangeDidChange,
    endDate,
    endTime,
    laterEndDate,
    laterEndTime,
    laterStartDate,
    laterStartTime,
  ]);

  const getFilterTimes = useCallback(
    (time: string) => filterEndTimes(endDate, time),
    [endDate],
  );
  return (
    <>
      <Text textStyle="bodyBold" fontSize={18} color="white" textAlign="left" mb="8" ml={1}>
        LnQ Parameters
      </Text>
      <Text color="lightgray" fontSize={16} mb={8}>
        Set up an alert for this code. <br /> The default time zone for this group is{" "}
        {group?.timeZone ? timeZoneFullNames[group.timeZone] : "unknown"}.
      </Text>
      <RadioGroup onChange={setCyActivation} value={cyActivation} colorScheme="brandYellow">
        <Stack
          justifyItems="flex-start"
          alignItems="left"
          direction="column"
          textColor="white"
          fontWeight="600"
          fontSize="16px"
          maxH="360px"
          px={0}
          pb={4}
          spacing="32px"
        >
          {/* Active Now Section */}
          <VStack align="left" spacing="12px">
            <Radio value={CYActivation.now}>Active now</Radio>
            <FormControl>
              <VStack spacing="12px" align="left">
                <Text color="lightgray" fontSize={14}>
                  End Time
                </Text>
                <HStack spacing="9px">
                  <DateRangePicker
                    buttonStyle={{ minW: "fit-content", h: "56px" }}
                    variant="single"
                    onDatesChange={(dates) => dates.length > 0 && setEndDate(dates[0])}
                    isDisabled={cyActivation !== CYActivation.now.toString()}
                    isDateSelectable={(date) =>
                      date.getTime() >= new Date().setHours(0, 0, 0, 0)
                    }
                    initialDates={endDate ? [endDate] : undefined}
                  />
                  <TimePicker
                    selectedTime={endTime ?? "End Time"}
                    setSelectedTime={setEndTime}
                    isDisabled={cyActivation !== CYActivation.now.toString() || !endDate}
                    filterTimes={getFilterTimes}
                    currentDate={endDate!}
                  />
                </HStack>
              </VStack>
            </FormControl>
          </VStack>

          {/* Active Later Section */}
          <VStack align="left" spacing="12px" w="400px">
            <Radio value={CYActivation.later}>Active later</Radio>
            <FormControl>
              <VStack spacing="12px" align="left">
                <Text color="lightgray" fontSize={14}>
                  Start Time
                </Text>
                <HStack spacing="9px">
                  <DateRangePicker
                    buttonStyle={{ minW: "fit-content", h: "56px" }}
                    variant="single"
                    onDatesChange={(dates) =>
                      dates.length > 0 && setLaterStartDate(dates[0])
                    }
                    isDisabled={cyActivation !== CYActivation.later.toString()}
                    isDateSelectable={(date) =>
                      date.getTime() >= new Date().setHours(0, 0, 0, 0)
                    }
                    initialDates={laterStartDate ? [laterStartDate] : undefined}
                  />
                  <TimePicker
                    selectedTime={laterStartTime ?? "Start Time"}
                    setSelectedTime={setLaterStartTime}
                    isDisabled={
                      cyActivation !== CYActivation.later.toString() || !laterStartDate
                    }
                    currentDate={laterStartDate!}
                  />
                </HStack>
                <Text color="lightgray" fontSize={14}>
                  End Time
                </Text>
                <HStack spacing="9px">
                  <DateRangePicker
                    buttonStyle={{ minW: "fit-content", h: "56px" }}
                    variant="single"
                    onDatesChange={(dates) => dates.length > 0 && setLaterEndDate(dates[0])}
                    isDisabled={cyActivation !== CYActivation.later.toString()}
                    isDateSelectable={(date) =>
                      date.getTime() >= (laterStartDate?.getTime() ?? Date.now())
                    }
                    initialDates={laterEndDate ? [laterEndDate] : undefined}
                  />
                  <TimePicker
                    selectedTime={laterEndTime ?? "End Time"}
                    setSelectedTime={setLaterEndTime}
                    isDisabled={
                      cyActivation !== CYActivation.later.toString() || !laterEndDate
                    }
                    currentDate={laterEndDate!}
                  />
                </HStack>
              </VStack>
            </FormControl>
          </VStack>
        </Stack>
      </RadioGroup>
    </>
  );
};

export default CYParametersStep;
